import './ModuleItem.scss';
import '../../../Tabs.scss';

import useSetPageTitle from 'utilities/useSetPageTitle';
import { AdminModuleTabs, AdminPagesRelativeRoutes } from 'Routes';
import { useServices } from 'index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useChaptersStore } from 'stores/Admin/Editors/ModuleEditor/ChaptersStore';
import { useGeneralStore } from 'stores/Admin/Editors/ModuleEditor/GeneralStore';
import { useCertificationsStore } from 'stores/Admin/Editors/ModuleEditor/CertificationsStore';
import { Button, Tab, Tabs } from '@trackman/web-shared-components';
import { NewAdminModule } from 'types/admin/modules';
import Box from '@mui/material/Box/Box';
import { useTabsStore } from 'stores/TabsStore';

export const ModuleItem = () => {
  const { activeTabIndex } = useTabsStore();
  const navigate = useNavigate();
  const moduleListService = useServices().moduleListService;

  const moduleId = useParams().moduleId ?? '';
  const { data, isPending } = useQuery({
    queryKey: ['adminModule', moduleId],
    queryFn: () => moduleListService.getModule(moduleId),
    enabled: !!moduleId,
    refetchOnWindowFocus: false,
  });

  const setInitialChapters = useChaptersStore((state) => state.setInitialChapters);
  const setInitialGeneralData = useGeneralStore((state) => state.setInitialData);
  const setInitialCertifications = useCertificationsStore((state) => state.setInitialCertifications);
  const resetChapters = useChaptersStore((state) => state.reset);
  const resetGeneralData = useGeneralStore((state) => state.reset);
  const resetCertifications = useCertificationsStore((state) => state.reset);
  const setInitializingGeneral = useGeneralStore((state) => state.setLoadingInitialData);
  const setInitializingChapters = useChaptersStore((state) => state.setLoadingInitialData);
  const setInitializingCertifications = useCertificationsStore((state) => state.setLoadingInitialData);
  const setLoadingGeneral = useGeneralStore((state) => state.setLoading);
  const setLoadingChapters = useChaptersStore((state) => state.setLoading);
  const setLoadingCertifications = useCertificationsStore((state) => state.setLoading);
  const loading = useGeneralStore((state) => state.loading);
  const title = useGeneralStore((state) => state.title);
  const shortDescription = useGeneralStore((state) => state.shortDescription);

  const queryClient = useQueryClient();
  useSetPageTitle({ title: data?.title ?? title });

  const saveModule = useMutation({
    mutationFn: () =>
      !!moduleId
        ? moduleListService.updateModule({ ...saveModulePayload, id: moduleId })
        : moduleListService.createModule(saveModulePayload),
    onMutate: () => {
      setLoadingGeneral(true);
      setLoadingChapters(true);
      setLoadingCertifications(true);
    },
    onSuccess: async (response) => {
      if (response) {
        navigate(`/Admin/${AdminPagesRelativeRoutes.ModuleItem}/${response}`);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['adminModuleList'] }),
        queryClient.invalidateQueries({ queryKey: ['learningHubModuleList'] }),
      ]);
    },
    onSettled: () => {
      setLoadingGeneral(false);
      setLoadingChapters(false);
      setLoadingCertifications(false);
    },
  });

  const navigateToModuleList = async () => {
    await Promise.all([queryClient.invalidateQueries({ queryKey: ['adminModuleList'] })]);
    navigate('/Admin/ModuleList');
  };

  const saveModulePayload: NewAdminModule = {
    title: useGeneralStore((state) => state.title),
    shortDescription: useGeneralStore((state) => state.shortDescription),
    fullDescription: useGeneralStore((state) => state.fullDescription),
    imageId: useGeneralStore((state) => state.imageId),
    status: useGeneralStore((state) => state.status),
    isOrganizationOnly: useGeneralStore((state) => state.isOrganizationOnly),
    allowedOrganizationIds: useGeneralStore((state) => state.allowedOrganizationIds),
    chapters: useChaptersStore((state) => state.chapters),
    certifications: useCertificationsStore((state) => state.certifications),
  };

  useEffect(() => {
    setInitializingGeneral(isPending && !!moduleId);
    setInitializingChapters(isPending && !!moduleId);
    setInitializingCertifications(isPending && !!moduleId);
    if (!isPending && data) {
      setInitialChapters(data.chapters ?? []);
      setInitialCertifications(data.certifications ?? []);
      setInitialGeneralData({
        id: data.id,
        title: data.title,
        shortDescription: data.shortDescription,
        fullDescription: data.fullDescription,
        imageId: data.imageId,
        status: data.status,
        isOrganizationOnly: data.isOrganizationOnly ?? false,
        allowedOrganizationIds: data.allowedOrganizationIds ?? [],
      });
    }
  }, [isPending, data]);

  useEffect(
    () => () => {
      resetCertifications();
      resetChapters();
      resetGeneralData();
    },
    []
  );

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>{data?.title ?? title}</h1>
        <span className='page-subtitle'>{data?.shortDescription ?? shortDescription}</span>
      </div>

      <Box className='tabs-container'>
        <Tabs scrollButtons='auto' value={activeTabIndex} variant='scrollable' indicatorColor='primary' allowScrollButtonsMobile>
          <Tab label='General' onClick={() => navigate(AdminModuleTabs.EditGeneral)} />
          <Tab label='Chapters' onClick={() => navigate(AdminModuleTabs.EditChapters)} />
          <Tab label='Certifications' onClick={() => navigate(AdminModuleTabs.EditCertifications)} />
        </Tabs>
        <div className='buttons'>
          <Button variant='contained' intent='tertiary' onClick={() => navigateToModuleList()} disabled={loading}>
            Cancel
          </Button>
          <Button variant='contained' intent='primary' onClick={() => saveModule.mutate()} disabled={loading} isLoading={loading}>
            Save
          </Button>
        </div>
      </Box>
      <Outlet />
    </>
  );
};
